/**
 * Session store — the call lifecycle state machine.
 *
 *   idle → starting → live-optimistic → connected → idle
 *
 * "live-optimistic" is load-bearing UX (lifted from production): the flag
 * flips the moment /calling/webcall succeeds — before transport.connect()
 * resolves. The call exists server-side at that point, so the calling layout
 * mounts one HTTP round trip after Start instead of waiting out the LiveKit
 * handshake. A connect failure rolls it back so the start screen (with the
 * error) returns instead of a dead calling layout.
 */
import { createStore } from "./store.js";
const NO_ERROR = { hasError: false, message: "" };
/** Verbatim production copy (use-microphone-permission.ts request() denial). */
const MIC_DENIED_MESSAGE = "Microphone access was denied. Please enable it in your browser settings.";
/** Verbatim production copy (RoomEvent.MediaDevicesError handler). */
const MEDIA_DEVICES_ERROR_MESSAGE = "Error acquiring microphone permission. Please make sure you grant the necessary permissions in your browser and reload the tab";
export function createSessionStore(api, transport, mic) {
    let phase = "idle";
    let isLoading = false;
    let connectionState = "disconnected";
    let error = NO_ERROR;
    const computeIsSessionLive = () => phase === "live-optimistic" || phase === "connected";
    const build = () => ({
        phase,
        isLoading,
        isSessionLive: computeIsSessionLive(),
        connectionState,
        error,
    });
    const handle = createStore(build);
    transport.onConnectionStateChange((state) => {
        connectionState = state;
        if (state === "connected" && phase !== "idle")
            phase = "connected";
        handle.commit();
    });
    // Mic acquisition failing after connect (permission revoked, device gone) —
    // production surfaces this via RoomEvent.MediaDevicesError → setError.
    transport.onMediaDevicesError(() => {
        error = { hasError: true, message: MEDIA_DEVICES_ERROR_MESSAGE };
        handle.commit();
    });
    return {
        getSnapshot: handle.getSnapshot,
        subscribe: handle.subscribe,
        dispose: handle.dispose,
        get isSessionLive() {
            return computeIsSessionLive();
        },
        prewarm() {
            void transport.prepareConnection(api.livekitUrl).catch(() => { });
        },
        async start(params) {
            if (params.mediaType === "audio" && !(await mic.isGranted())) {
                if (!(await mic.request())) {
                    // Denial must not be silent: production's mic.onError puts this
                    // message on the start screen (use-microphone-permission.ts).
                    error = { hasError: true, message: MIC_DENIED_MESSAGE };
                    handle.commit();
                    return null;
                }
            }
            isLoading = true;
            phase = "starting";
            handle.commit();
            try {
                const response = await api.startWebcall({
                    agentId: params.agentId,
                    variables: params.variables,
                    mediaType: params.mediaType,
                });
                // Call exists server-side — mount the calling layout now.
                phase = "live-optimistic";
                error = NO_ERROR;
                handle.commit();
                await transport.connect(api.livekitUrl, response.user_token);
                if (params.mediaType === "audio")
                    await transport.setMicrophoneEnabled(true);
                return {
                    callId: response.call_id,
                    slashCommands: response.enabled_slash_commands ?? [],
                };
            }
            catch (err) {
                // Roll the optimistic flip back — see module docs.
                phase = "idle";
                error = { hasError: true, message: err.message };
                handle.commit();
                return null;
            }
            finally {
                isLoading = false;
                handle.commit();
            }
        },
        async end() {
            isLoading = true;
            handle.commit();
            try {
                await transport.disconnect();
            }
            catch {
                // Disconnect failures don't block teardown.
            }
            finally {
                phase = "idle";
                isLoading = false;
                handle.commit();
            }
        },
        clearError() {
            error = NO_ERROR;
            handle.commit();
        },
    };
}
